import { useMemo } from 'react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

/**
 * Quick Help options for Dashboard
 * Shows floating (?) button with common actions
 * Uses multi-step tours to guide users through sidebar navigation
 */
export const useDashboardQuickHelp = () => {
    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'create-dayoff',
                label: 'Tạo phiếu nghỉ phép',
                description: 'Đăng ký nghỉ phép, nghỉ bù',
                tourTitle: 'Tạo phiếu nghỉ phép',
                tourDescription: '',
                // Multi-step tour: Sidebar → Child menu → Create button
                tourSteps: [
                    {
                        selector: '[data-tour="sidebar-parent-account"]',
                        title: 'Bước 1: Mở menu Tài khoản',
                        content:
                            'Click vào mục "Tài khoản" trên sidebar để mở menu con.',
                        position: 'right' as const,
                        demo: 'sidebar-menu-expand-dayoff' as const,
                    },
                    {
                        selector: '[data-tour="sidebar-account-dayoffs"]',
                        title: 'Bước 2: Chọn Nghỉ phép',
                        content:
                            'Click vào "Nghỉ phép" để vào trang quản lý nghỉ phép.',
                        position: 'right' as const,
                        demo: 'sidebar-submenu-select-dayoff' as const,
                    },
                    {
                        selector: '[data-tour="create-dayoff-btn"]',
                        title: 'Bước 3: Tạo phiếu nghỉ phép',
                        content:
                            'Click nút "Tạo mới" để tạo phiếu nghỉ phép.\nChọn loại nghỉ phép, nhập thời gian và lý do.',
                        position: 'bottom' as const,
                        demo: 'click-create-button' as const,
                    },
                ],
            },
            {
                id: 'create-business',
                label: 'Tạo phiếu công tác',
                description: 'Đăng ký công tác, gặp khách hàng',
                tourTitle: 'Tạo phiếu công tác',
                tourDescription: '',
                // Multi-step tour: Sidebar → Child menu → Create button
                tourSteps: [
                    {
                        selector: '[data-tour="sidebar-parent-account"]',
                        title: 'Bước 1: Mở menu Tài khoản',
                        content:
                            'Click vào mục "Tài khoản" trên sidebar để mở menu con.',
                        position: 'right' as const,
                        demo: 'sidebar-menu-expand-business' as const,
                    },
                    {
                        selector: '[data-tour="sidebar-account-businesses"]',
                        title: 'Bước 2: Chọn Công tác',
                        content:
                            'Click vào "Công tác" để vào trang quản lý công tác.',
                        position: 'right' as const,
                        demo: 'sidebar-submenu-select-business' as const,
                    },
                    {
                        selector: '[data-tour="create-business-btn"]',
                        title: 'Bước 3: Tạo phiếu công tác',
                        content:
                            'Click nút "Tạo mới" để tạo phiếu công tác.\nNhập thông tin công tác, khách hàng cần gặp.',
                        position: 'bottom' as const,
                        demo: 'click-create-button' as const,
                    },
                ],
            },
            {
                id: 'create-customer',
                label: 'Tạo khách hàng mới',
                description: 'Thêm khách hàng vào hệ thống',
                tourTitle: 'Tạo khách hàng mới',
                tourDescription: '',
                // Multi-step tour: Sidebar Khách hàng → Create options
                tourSteps: [
                    {
                        selector: '[data-tour="sidebar-parent-customers"]',
                        title: 'Bước 1: Vào trang Khách hàng',
                        content:
                            'Click vào mục "Khách hàng" trên sidebar để vào trang quản lý khách hàng.',
                        position: 'right' as const,
                        demo: 'sidebar-click-customer' as const,
                    },
                    {
                        selector: '[data-tour="create-customer-btn"]',
                        title: 'Bước 2: Tạo khách hàng',
                        content:
                            'Có 2 cách tạo khách hàng:\n\n1. Click "Tạo khách hàng" để nhập form trực tiếp\n2. Tải "Mẫu Template" → điền thông tin → "Import khách hàng"',
                        position: 'bottom' as const,
                        demo: 'customer-create-options' as const,
                    },
                ],
            },
            {
                id: 'create-contract',
                label: 'Tạo hợp đồng',
                description: 'Tạo hợp đồng mới cho khách hàng',
                tourTitle: 'Tạo hợp đồng từ trang Khách hàng',
                tourDescription: '',
                // Multi-step tour: Sidebar Khách hàng → Select customer → QTBH tab → Create contract
                tourSteps: [
                    {
                        selector: '[data-tour="sidebar-parent-customers"]',
                        title: 'Bước 1: Vào trang Khách hàng',
                        content:
                            'Click vào mục "Khách hàng" trên sidebar để vào trang quản lý khách hàng.',
                        position: 'right' as const,
                        demo: 'sidebar-click-customer' as const,
                    },
                    {
                        selector: '[data-tour="customer-list"]',
                        title: 'Bước 2: Chọn khách hàng',
                        content:
                            'Tìm kiếm và click vào khách hàng cần tạo hợp đồng.',
                        position: 'bottom' as const,
                        demo: 'customer-select-row' as const,
                    },
                    {
                        selector: '[data-tour="customer-detail-tabs"]',
                        title: 'Bước 3: Chọn tab Quá trình bán hàng',
                        content:
                            'Click vào tab "Quá trình bán hàng" để xem và quản lý các hợp đồng.',
                        position: 'bottom' as const,
                        demo: 'customer-detail-tabs' as const,
                    },
                    {
                        selector: '[data-tour="create-contract-btn"]',
                        title: 'Bước 4: Tạo hợp đồng',
                        content:
                            'Click nút "Tạo hợp đồng" để tạo hợp đồng mới cho khách hàng.',
                        position: 'bottom' as const,
                        demo: 'contract-create-button' as const,
                    },
                ],
            },
            {
                id: 'create-poc',
                label: 'Tạo POC',
                description: 'Tạo dịch vụ dùng thử cho khách hàng',
                tourTitle: 'Tạo POC từ trang Khách hàng',
                tourDescription: '',
                // Multi-step tour: Sidebar Khách hàng → Select customer → QTBH tab → Create POC
                tourSteps: [
                    {
                        selector: '[data-tour="sidebar-parent-customers"]',
                        title: 'Bước 1: Vào trang Khách hàng',
                        content:
                            'Click vào mục "Khách hàng" trên sidebar để vào trang quản lý khách hàng.',
                        position: 'right' as const,
                        demo: 'sidebar-click-customer' as const,
                    },
                    {
                        selector: '[data-tour="customer-list"]',
                        title: 'Bước 2: Chọn khách hàng',
                        content:
                            'Tìm kiếm và click vào khách hàng cần tạo POC.',
                        position: 'bottom' as const,
                        demo: 'customer-select-row' as const,
                    },
                    {
                        selector: '[data-tour="customer-detail-tabs"]',
                        title: 'Bước 3: Chọn tab Quá trình bán hàng',
                        content:
                            'Click vào tab "Quá trình bán hàng" để xem pipeline bán hàng.',
                        position: 'bottom' as const,
                        demo: 'customer-detail-tabs' as const,
                    },
                    {
                        selector: '[data-tour="create-poc-btn"]',
                        title: 'Bước 4: Tạo POC',
                        content:
                            'Click nút "Tạo POC" để tạo dịch vụ dùng thử.\nCần ở bước POC trong pipeline.',
                        position: 'bottom' as const,
                        demo: 'poc-create-button' as const,
                    },
                ],
            },
        ],
        []
    )

    useQuickHelp(quickHelpOptions)
}

export default useDashboardQuickHelp
